'use client';

// ContentSummary - Display total sources and character count with proceed button

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@boty/ui';
import Chip from '@mui/material/Chip';
import { useTranslations } from 'next-intl';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import type { ContentSourceSummary } from '../../types/content-source';
import type { SourceType } from '@boty/database';
import { formatNumber } from '../../lib/format';

export interface ContentSummaryProps {
  /** Summary data */
  summary: ContentSourceSummary | null;
  /** Whether any content is still processing */
  isProcessing: boolean;
  /** Whether all content is ready */
  allReady: boolean;
  /** Callback when proceed button is clicked */
  onProceed: () => void;
  /** Whether proceed is disabled */
  disabled?: boolean;
  /** Custom label for the proceed button */
  proceedButtonLabel?: string | undefined;
  /** Custom icon for the proceed button */
  proceedButtonIcon?: React.ReactNode | undefined;
}

// Icons for source types
const TYPE_ICONS: Record<SourceType, React.ElementType> = {
  text: TextFieldsIcon,
  clarification: QuestionAnswerIcon,
  file: InsertDriveFileIcon,
  image: ImageIcon,
  url: LinkIcon,
  google_sheets: TableChartIcon,
  google_docs: DescriptionIcon,
};

// i18n keys for source type labels
const TYPE_LABEL_KEYS: Record<SourceType, string> = {
  text: 'text',
  clarification: 'clarification',
  file: 'file',
  image: 'image',
  url: 'url',
  google_sheets: 'google_sheets',
  google_docs: 'google_docs',
};

/**
 * ContentSummary - Display summary of content sources with proceed button
 *
 * Features:
 * - Total sources count with breakdown by type
 * - Total character count
 * - Type breakdown chips
 * - Proceed to Testing button
 * - Processing status indicator
 * - RTL support
 *
 * @example
 * ```tsx
 * <ContentSummary
 *   summary={summary}
 *   isProcessing={isProcessing}
 *   allReady={allReady}
 *   onProceed={() => router.push('/test')}
 * />
 * ```
 */
export function ContentSummary({
  summary,
  isProcessing,
  allReady,
  onProceed,
  disabled = false,
  proceedButtonLabel,
  proceedButtonIcon,
}: ContentSummaryProps) {
  const t = useTranslations('creator.contentSources.summary');
  const tTypes = useTranslations('creator.contentSources.types');

  // Don't render if no summary
  if (!summary || summary.totalSources === 0) {
    return null;
  }

  // Get active source types (count > 0)
  const activeTypes = (Object.entries(summary.byType) as [SourceType, number][])
    .filter(([, count]) => count > 0);

  return (
    <Box
      sx={{
        py: 1,
      }}
    >
      {/* Summary stats */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'baseline',
        }}
      >
        {/* Total sources */}
        <Typography variant="body2" color="text.secondary">
          <Typography component="span" fontWeight="bold" color="text.primary">
            {summary.totalSources}
          </Typography>{' '}
          {t('totalSources', { count: summary.totalSources })}
        </Typography>

        {/* Total characters */}
        <Typography variant="body2" color="text.secondary">
          <Typography component="span" fontWeight="bold" color="text.primary">
            {formatNumber(summary.totalCharacters)}
          </Typography>{' '}
          {t('totalCharacters', { count: summary.totalCharacters })}
        </Typography>
      </Box>

      {/* Type breakdown chips */}
      {activeTypes.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {activeTypes.map(([type, count]) => {
            const Icon = TYPE_ICONS[type];
            return (
              <Chip
                key={type}
                size="small"
                icon={<Icon sx={{ fontSize: 16 }} />}
                label={`${count} ${tTypes(TYPE_LABEL_KEYS[type])}`}
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            );
          })}
        </Box>
      )}

      {/* Proceed button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onProceed}
          disabled={disabled || isProcessing || !allReady}
          loading={isProcessing}
          startIcon={!isProcessing ? proceedButtonIcon : undefined}
        >
          {isProcessing ? t('stillProcessing') : (proceedButtonLabel || t('proceedToTesting'))}
        </Button>
      </Box>
    </Box>
  );
}

export default ContentSummary;
